import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Box, Typography, Avatar, useTheme, Grid } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChatImage from './ChatImage';
import LinkifyText from './LinkifyText';
import { 
  extractImagesFromMarkdown, 
  stripImageMarkdown,
  isProjectContent,
  isSkillsContent,
  extractTechIcons
} from '../utils/markdownUtils';

interface ChatMessageProps {
  message: string;
  isUser: boolean;
  timestamp: Date;
  onTypingStateChange?: (isTyping: boolean) => void;
  messageId?: string; // Adding optional messageId prop to prevent restarting typing on re-renders
}

const ChatMessage: React.FC<ChatMessageProps> = ({ 
  message, 
  isUser, 
  timestamp,
  onTypingStateChange,
  messageId
}) => {
  const theme = useTheme();
  const [isTypingComplete, setIsTypingComplete] = useState(isUser); // Always complete for user messages
  const [displayedText, setDisplayedText] = useState('');
  const messageIdRef = useRef<string | undefined>(messageId);
  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Extract images from message content
  const messageImages = useMemo(() => {
    // Only extract images for assistant messages that contain project or skills info
    if (!isUser && (isProjectContent(message) || isSkillsContent(message))) {
      return extractImagesFromMarkdown(message);
    }
    return [];
  }, [message, isUser]);
  
  // Extract tech icons from message content
  const techIcons = useMemo(() => {
    if (!isUser) {
      return extractTechIcons(message);
    }
    return [];
  }, [message, isUser]);
  
  // Clean message text by removing image markdown but preserving alt text
  const cleanedMessage = useMemo(() => {
    if (isUser) return message;
    // Remove both standard images and tech icons from the displayed text
    let cleaned = stripImageMarkdown(message);
    // Also strip tech icon format: Technology ![alt](url)
    cleaned = cleaned.replace(/(\w+)\s*!\[([^\]]*)\]\([^)]+\)/g, '$1');
    return cleaned;
  }, [message, isUser]);

  // Handle notifying parent about typing state changes in a stable way
  const notifyTypingChange = useMemo(() => {
    return (isTyping: boolean) => {
      if (onTypingStateChange) {
        onTypingStateChange(isTyping);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  // Create typing effect for assistant messages
  useEffect(() => {
    // If message ID changed, update ref to prevent stale closures
    if (messageId !== messageIdRef.current) {
      messageIdRef.current = messageId;
    }
    
    if (isUser) {
      setDisplayedText(cleanedMessage);
      setIsTypingComplete(true);
      return;
    }
    
    // Reset typing state when a new message comes in
    setDisplayedText('');
    setIsTypingComplete(false);
    
    // Notify parent component that typing has started
    notifyTypingChange(true);
    
    let index = 0;
    const typingSpeed = 10; // faster typing speed
    
    // Clear any existing timer
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
    }
    
    typingTimerRef.current = setInterval(() => {
      if (index < cleanedMessage.length) {
        setDisplayedText(cleanedMessage.substring(0, index + 1));
        index++;
      } else {
        if (typingTimerRef.current) {
          clearInterval(typingTimerRef.current);
          typingTimerRef.current = null;
        }
        setIsTypingComplete(true);
        
        // Notify parent component that typing has completed
        notifyTypingChange(false);
      }
    }, typingSpeed);
    
    return () => {
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
        
        // Ensure we reset the typing state if component unmounts during typing
        if (!isTypingComplete) {
          notifyTypingChange(false);
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUser, cleanedMessage, message]); // Removed onTypingStateChange from dependencies

  // Get background color based on message type and theme mode
  const getBackgroundColor = () => {
    if (isUser) {
      return 'transparent';
    } else {
      return theme.palette.mode === 'dark' 
        ? 'rgba(68, 70, 84, 0.3)' 
        : 'rgba(247, 247, 248, 0.8)';
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        py: 4,
        px: 3,
        backgroundColor: getBackgroundColor(),
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          maxWidth: '700px',
          mx: 'auto',
          gap: 2,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          {isUser ? (
            <Avatar 
              sx={{ 
                bgcolor: theme.palette.primary.main,
                width: 30, 
                height: 30 
              }}
            >
              <PersonIcon fontSize="small" />
            </Avatar>
          ) : (
            <Avatar 
              sx={{ 
                bgcolor: theme.palette.mode === 'dark' ? '#10a37f' : '#19c37d',
                width: 30, 
                height: 30 
              }}
            >
              <SmartToyIcon fontSize="small" />
            </Avatar>
          )}
        </Box>
        
        <Box sx={{ width: 'calc(100% - 46px)' }}>
          {/* Message text content with typing effect for assistant messages */}
          <Box sx={{ mb: messageImages.length > 0 || techIcons.length > 0 ? 2 : 0 }}>
            <LinkifyText 
              text={displayedText}
              targetBlank={true}
              variant="body1"
            />
            {!isTypingComplete && !isUser && (
              <Box component="span" sx={{ display: 'inline-block', width: '0.5em', height: '1.2em', borderRight: '2px solid', animation: 'blink-caret 0.75s step-end infinite', '@keyframes blink-caret': { '50%': { borderColor: 'transparent' } } }}></Box>
            )}
          </Box>
          
          {/* Only show images and tech icons after typing is complete */}
          {isTypingComplete && (
            <>
              {/* Image gallery for content that likely describes projects or skills */}
              {messageImages.length > 0 && (
                <Box mt={2}>
                  <Grid container spacing={1}>
                    {messageImages.map((img, index) => (
                      <Grid item xs={12} sm={6} key={`${img.src}-${index}`}>
                        <ChatImage 
                          src={img.src} 
                          alt={img.alt} 
                          caption={img.caption || img.alt} 
                          maxWidth="100%" 
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {messageImages.length > 4 && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                    >
                      And {messageImages.length - 4} more images available...
                    </Typography>
                  )}
                </Box>
              )}
              
              {/* Display tech icons if available */}
              {techIcons.length > 0 && (
                <Grid container spacing={1} sx={{ mt: 2 }}>
                  {techIcons.map((icon, index) => (
                    <Grid item key={`icon-${index}`}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          mr: 1,
                        }}
                      >
                        <ChatImage 
                          src={icon.src}
                          alt={icon.alt}
                          maxWidth="32px"
                        />
                        <Typography variant="caption" align="center" sx={{ mt: 0.5 }}>
                          {icon.name}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatMessage;